/* eslint-disable import/no-cycle */
import axios, { AxiosRequestConfig } from 'axios';
import { debounce } from 'lodash';
import { AuthHelper, getToken } from './shared/utils';
import { ExceptionTypes, ResponseErrorWithHandled } from './shared/types';
import { showValidationErrorNotification } from './shared/utils/errors';
import history from './shared/history';
import { planError } from './shared/utils/errors/plan-permission-error/plan-error';
import { showGeneralErrorToaster } from './shared/utils/errors/system-error-toaster';
import { SystemErrors } from './shared/enums/system-errors';
import {
  isApiLockedForMigration,
  MIGRATION_API_LOCK_MESSAGE,
} from './shared/utils/migration-api-lock';

export interface ApiRequestConfig extends AxiosRequestConfig {
  skipGlobalErrorToast?: boolean;
}

const instance = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
});

instance.interceptors.request.use(
  (config) => {
    // Migrated users only see the V2 redirect overlay — block every API call.
    if (isApiLockedForMigration()) {
      return Promise.reject(new axios.Cancel(MIGRATION_API_LOCK_MESSAGE));
    }

    const token = getToken();
    if (token) {
      config.headers.authorization = `Bearer ${token}`;
    }
    return config;
  },
  (err) => Promise.reject(err),
);

const isCustomError = (err: any): boolean => !!err.response?.data?.type;

const debounceSystemError = debounce((errorMessage) => {
  showGeneralErrorToaster(errorMessage);
}, 500);

const handleSystemError = (errorMessage?: string) => {
  debounceSystemError(errorMessage || SystemErrors.InternalServerError);
};

instance.interceptors.response.use(
  (response) => response.data,
  (err): Promise<ResponseErrorWithHandled> => {
    if (axios.isCancel(err) && err.message === MIGRATION_API_LOCK_MESSAGE) {
      const lockedError: ResponseErrorWithHandled = {
        error: true,
        type: ExceptionTypes.System,
        message: '',
        isHandled: true,
      };
      return Promise.reject(lockedError);
    }

    const skipGlobalErrorToast = Boolean(
      (err.config as ApiRequestConfig | undefined)?.skipGlobalErrorToast,
    );
    let error: ResponseErrorWithHandled;

    if (isCustomError(err)) {
      error = { ...err.response.data, isHandled: false };

      // eslint-disable-next-line default-case
      switch (error.type) {
        case ExceptionTypes.Auth:
          AuthHelper.logout({ fallbackTo: history.location });
          break;

        case ExceptionTypes.System:
          handleSystemError();
          error.isHandled = true;
          break;

        case ExceptionTypes.General:
          handleSystemError(error.message);
          error.isHandled = true;
          break;

        case ExceptionTypes.Validation: {
          showValidationErrorNotification(error.messages);
          error.isHandled = true;
          break;
        }
        case ExceptionTypes.PlanPermission: {
          planError(error.code, error.message);
          error.isHandled = true;
          break;
        }
      }
    } else {
      error = {
        error: true,
        type: ExceptionTypes.System,
        message: err.message,
        isHandled: skipGlobalErrorToast,
      };

      if (!skipGlobalErrorToast) {
        handleSystemError(error.message);
        error.isHandled = true;
      }
    }

    return Promise.reject(error);
  },
);

export default instance;
